import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { HotTable } from '@handsontable/react-wrapper';
import { registerAllModules } from 'handsontable/registry';
import Handsontable from 'handsontable';

import 'handsontable/styles/handsontable.css';
import 'handsontable/styles/ht-theme-main.css';
import 'handsontable/styles/ht-theme-horizon.css';
import './HandsontableStyles.css';

registerAllModules();

// Inject a tiny CSS spinner once, so the grid can render a full-cell loader without external styles
const ensureSpinnerStylesInjected = (() => {
    let injected = false;
    return () => {
        if (injected || typeof document === 'undefined') return;
        const style = document.createElement('style');
        style.id = 'htdg-spinner-styles';
        style.textContent = `
.htdg-spinner-wrapper{display:flex;align-items:center;justify-content:center;width:100%;height:100%;min-height:20px}
.htdg-spinner{width:18px;height:18px;border:2px solid rgba(0,0,0,.15);border-top-color:rgba(0,0,0,.6);border-radius:50%;animation:htdg-spin .8s linear infinite}
@keyframes htdg-spin{to{transform:rotate(360deg)}}`;
        document.head.appendChild(style);
        injected = true;
    };
})();

const SUPPORTED_THEMES = {
    'ht-theme-main': 'ht-theme-main',
    'ht-theme-horizon': 'ht-theme-horizon',
};

/**
 * @param {Array} data
 * @param {Function} onUpdateCell
 * @param {Array|boolean} colHeaders
 * @param {Array} columns - Required array of column definitions. Each column must have:
 *                         - data: string (the property name in the data object)
 *                         - type: string ('text'|'numeric'|'checkbox'|'date')
 *                         - Other optional Handsontable column properties
 * @param {boolean} selectable
 * @param {Array} selectedRows
 * @param {Function} onSelectionChange
 * @param {string} themeName
 * @param {string} variant
 * @param {string} size
 * @param {string} className
 * @param {Object} handsontableSettings
 */
const HandsontableDataGrid = ({
    data = [],
    colHeaders = null,
    columns, // Remove the default null value to make it required
    selectable = false,
    selectedRows = null,
    onSelectionChange = null,
    themeName = 'ht-theme-horizon',
    variant = 'default',
    size = 'default',
    className = '',
    handsontableSettings = {},
    afterChange = null,
}) => {
    const hotRef = useRef(null);
    const [internalData, setInternalData] = useState([]);
    const [internalSelectedRows, setInternalSelectedRows] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        ensureSpinnerStylesInjected();
    }, []);

    const isControlledSelection = selectedRows !== null;
    const currentSelectedRows = isControlledSelection ? selectedRows : internalSelectedRows;

    const validatedThemeName = useMemo(() => {
        if (SUPPORTED_THEMES[themeName]) {
            return SUPPORTED_THEMES[themeName];
        }
        return SUPPORTED_THEMES['ht-theme-horizon'];
    }, [themeName]);

    useEffect(() => {
        setInternalData([...data]);

        if (!isControlledSelection) {
            setInternalSelectedRows([]);
        }
    }, [data, isControlledSelection]);

    const generateHeaders = useCallback((dataArray) => {
        if (!dataArray || dataArray.length === 0) return [];

        const firstObject = dataArray[0];
        return Object.keys(firstObject).map((key) => {
            return key
                .replace(/([A-Z])/g, ' $1')
                .replace(/_/g, ' ')
                .replace(/^./, (str) => str.toUpperCase())
                .trim();
        });
    }, []);

    const handleSelectionChange = useCallback(
        (newSelectedIndices) => {
            if (!isControlledSelection) {
                setInternalSelectedRows(newSelectedIndices);
            }

            if (onSelectionChange) {
                const selectedRowData = newSelectedIndices.map((index) => internalData[index]).filter(Boolean);
                onSelectionChange(newSelectedIndices, selectedRowData);
            }
        },
        [isControlledSelection, onSelectionChange, internalData],
    );

    const selectionRenderer = useCallback(
        (instance, td, row, col, prop, value, cellProperties) => {
            try {
                td.innerHTML = '';
                td.className = 'ht-cell-selection';

                const checkbox = document.createElement('input');
                checkbox.type = 'checkbox';
                checkbox.className = 'ht-selection-checkbox';
                checkbox.checked = currentSelectedRows.includes(row);

                checkbox.addEventListener('change', (e) => {
                    e.stopPropagation();
                    const isChecked = e.target.checked;
                    let newSelectedRows;

                    if (isChecked) {
                        newSelectedRows = [...currentSelectedRows, row].sort((a, b) => a - b);
                    } else {
                        newSelectedRows = currentSelectedRows.filter((r) => r !== row);
                    }

                    handleSelectionChange(newSelectedRows);
                });

                td.appendChild(checkbox);

                cellProperties.readOnly = true;
            } catch (error) {
                td.innerHTML = '';
            }

            return td;
        },
        [currentSelectedRows, handleSelectionChange],
    );

    const selectAllRenderer = useCallback(
        (instance, td, row, col, prop, value, cellProperties) => {
            try {
                td.innerHTML = '';
                td.className = 'ht-cell-select-all';

                const checkbox = document.createElement('input');
                checkbox.type = 'checkbox';
                checkbox.className = 'ht-select-all-checkbox';

                const totalRows = internalData.length;
                const selectedCount = currentSelectedRows.length;

                if (selectedCount === 0) {
                    checkbox.checked = false;
                    checkbox.indeterminate = false;
                } else if (selectedCount === totalRows) {
                    checkbox.checked = true;
                    checkbox.indeterminate = false;
                } else {
                    checkbox.checked = false;
                    checkbox.indeterminate = true;
                }

                checkbox.addEventListener('change', (e) => {
                    e.stopPropagation();
                    const isChecked = e.target.checked;

                    if (isChecked || checkbox.indeterminate) {
                        const allIndices = Array.from({ length: totalRows }, (_, i) => i);
                        handleSelectionChange(allIndices);
                    } else {
                        handleSelectionChange([]);
                    }
                });

                td.appendChild(checkbox);
            } catch (error) {
                td.innerHTML = '';
            }

            return td;
        },
        [currentSelectedRows, internalData.length, handleSelectionChange],
    );

    const editabilityRenderer = useCallback(
        (instance, td, row, col, prop, value, cellProperties) => {
            try {
                let baseRenderer;

                if (Handsontable?.renderers) {
                    switch (cellProperties.type) {
                        case 'numeric':
                            baseRenderer = Handsontable.renderers.NumericRenderer;
                            break;
                        case 'checkbox':
                            baseRenderer = Handsontable.renderers.CheckboxRenderer;
                            break;
                        case 'date':
                            baseRenderer = Handsontable.renderers.TextRenderer;
                            break;
                        default:
                            baseRenderer = Handsontable.renderers.TextRenderer;
                    }
                }

                if (baseRenderer) {
                    baseRenderer(instance, td, row, col, prop, value, cellProperties);
                } else {
                    td.innerHTML = value != null ? String(value) : '';
                }

                const isReadOnly = cellProperties.readOnly || isLoading;

                td.classList.remove('ht-cell-readonly', 'ht-cell-editable');

                if (isReadOnly) {
                    td.classList.add('ht-cell-readonly');
                    td.removeAttribute('data-editable');
                } else {
                    td.classList.add('ht-cell-editable');
                    td.setAttribute('data-editable', 'true');
                }

                if (cellProperties.type === 'numeric') {
                    td.classList.add('ht-cell-numeric');
                } else if (cellProperties.type === 'checkbox') {
                    td.classList.add('ht-cell-checkbox');
                } else if (cellProperties.type === 'date') {
                    td.classList.add('ht-cell-date');
                }
            } catch (error) {
                td.innerHTML = value != null ? String(value) : '';
            }

            return td;
        },
        [isLoading],
    );

    const effectiveColHeaders = useMemo(() => {
        const baseHeaders = colHeaders || generateHeaders(data);

        if (selectable) {
            return ['', ...baseHeaders];
        }

        return baseHeaders;
    }, [colHeaders, generateHeaders, data, selectable]);

    const effectiveColumns = useMemo(() => {
        if (!columns || columns.length === 0) {
            throw new Error('Column definitions are required for HandsontableDataGrid');
        }

        const enhancedColumns = columns.map((column) => {
            const isDateColumn = column.type === 'date';

            return {
                ...column,
                renderer: column.renderer || editabilityRenderer,
                ...(isDateColumn && {
                    dateFormat: 'MM/DD/YYYY',
                    correctFormat: true,
                    datePickerConfig: {
                        format: 'mm/dd/yyyy',
                        showWeekNumber: true,
                        ...column.datePickerConfig,
                    },
                }),
            };
        });

        if (selectable) {
            const selectionColumn = {
                data: '__selection',
                renderer: selectionRenderer,
                readOnly: true,
                width: 50,
                className: 'ht-selection-column',
            };

            return [selectionColumn, ...enhancedColumns];
        }

        return enhancedColumns;
    }, [columns, selectable, editabilityRenderer, selectionRenderer]);

    const afterRenderer = useCallback(
        (TD, row, col, prop, value, cellProperties) => {
            if (prop === '__selection' && selectable) {
                TD.classList.add('ht-cell-selection');
                return;
            }

            // When a cell is updating, replace its content with a centered inline SVG spinner
            if (cellProperties && cellProperties.isUpdating) {
                // Ensure no editable affordances or type-specific classes show
                TD.removeAttribute('data-editable');
                TD.classList.remove('ht-cell-editable', 'ht-cell-numeric', 'ht-cell-checkbox', 'ht-cell-date');

                // Full-cell spinner using a lightweight CSS-based loader (styles injected once at runtime)
                TD.innerHTML =
                    '<div class="htdg-spinner-wrapper" role="status" aria-label="Saving"><div class="htdg-spinner"></div></div>';
                return;
            }

            if (!cellProperties.renderer || cellProperties.renderer === 'editabilityRenderer') {
                const isReadOnly = cellProperties.readOnly || isLoading;

                TD.classList.remove('ht-cell-readonly', 'ht-cell-editable');

                if (isReadOnly) {
                    TD.classList.add('ht-cell-readonly');
                    TD.removeAttribute('data-editable');
                } else {
                    TD.classList.add('ht-cell-editable');
                    TD.setAttribute('data-editable', 'true');
                }

                if (cellProperties.type === 'numeric') {
                    TD.classList.add('ht-cell-numeric');
                } else if (cellProperties.type === 'checkbox') {
                    TD.classList.add('ht-cell-checkbox');
                } else if (cellProperties.type === 'date') {
                    TD.classList.add('ht-cell-date');
                }
            }

            // Clear any residual loading attributes if previously set
            TD.classList.remove('ht-cell-loading');
            TD.removeAttribute('data-loading');
        },
        [isLoading, selectable],
    );

    const afterGetColHeader = useCallback(
        (col, TH) => {
            if (selectable && col === 0) {
                selectAllRenderer(null, TH, -1, col, '__selectAll', null, {});
            }
        },
        [selectable, selectAllRenderer],
    );

    useEffect(() => {
        if (!selectable || !hotRef.current?.hotInstance) return;

        const hotInstance = hotRef.current.hotInstance;
        const rowCount = hotInstance.countRows();

        for (let i = 0; i < rowCount; i++) {
            const rowElement = hotInstance.getCell(i, 0)?.parentElement;
            if (rowElement) {
                rowElement.classList.remove('ht-row-selected');
            }
        }

        currentSelectedRows.forEach((rowIndex) => {
            if (rowIndex < rowCount) {
                const rowElement = hotInstance.getCell(rowIndex, 0)?.parentElement;
                if (rowElement) {
                    rowElement.classList.add('ht-row-selected');
                }
            }
        });
    }, [selectable, currentSelectedRows, internalData]);

    const isEffectivelyLoading = isLoading;

    const effectiveClassName = [
        validatedThemeName,
        className,
        isEffectivelyLoading ? 'ht-loading' : '',
        selectable ? 'ht-selectable' : '',
        variant !== 'default' ? `ht-variant-${variant}` : '',
        size !== 'default' ? `ht-size-${size}` : '',
    ]
        .filter(Boolean)
        .join(' ');

    const defaultSettings = {
        licenseKey: 'non-commercial-and-evaluation',
        height: 'auto',
        width: '100%',
        stretchH: 'all',
        autoColumnSize: false,
        rowHeaders: true,
        colHeaders: effectiveColHeaders,
        columns: effectiveColumns,
        contextMenu: false,
        manualRowResize: true,
        manualColumnResize: true,
        wordWrap: true,
        manualRowMove: false,
        manualColumnMove: false,
        columnSorting: true,
        dropdownMenu: false,
        filters: true,
        navigableHeaders: true,
        headerClassName: 'htLeft',
        afterChange: afterChange,
        afterRenderer: afterRenderer,
        afterGetColHeader: selectable ? afterGetColHeader : undefined,
        className: effectiveClassName,
        readOnly: isEffectivelyLoading,

        ...handsontableSettings,
    };

    return (
        <div>
            {isEffectivelyLoading && <div className="loading-overlay">Loading...</div>}
            <HotTable ref={hotRef} data={internalData} {...defaultSettings} />
        </div>
    );
};

export { HandsontableDataGrid };
